import { Observable, Subject, catchError, map, of } from 'rxjs'
import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'

import {
  ICutoffConfigurationList,
  ICutoffConfigurationResponse,
  ICreateCutoffConfigurationDto,
  IUpdateCutoffConfigurationDto,
  ICutoffConfigFilterOptions,
} from '../../interfaces/wallet-config.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

export interface IWalletConfigResponse {
  error: boolean
  msg: string
  data: any
}

export interface IWalletConfigList {
  error: boolean
  msg: string
  data: {
    data: any[]
    totalItems: number
    page: number
    itemsByPage: number
  }
  status: number
}

@Injectable({
  providedIn: 'root',
})
export class ApiWalletConfigService extends ApiClass {
  private refreshSubject = new Subject<void>()
  public refresh$ = this.refreshSubject.asObservable()
  private userId: string = ''

  constructor(private authService: AuthService) {
    super()
    this.path = 'wallet/config'
    this.userId = this.authService.getUser?.user.id || ''
  }

  // Obtener historial de reportes con filtros y paginación
  getAll(page = 1, limit = 10, filters = {}): Observable<IWalletConfigList> {
    const response: IWalletConfigList = {
      error: true,
      msg: '',
      data: { data: [], totalItems: 0, page: 1, itemsByPage: 10 },
      status: 500,
    }

    const body = { page, limit, filters }

    return this.http.post<IWalletConfigResponse>(`${environment.apiUrl}${this.path}/history`, body).pipe(
      map((apiResponse) => {
        if (apiResponse.error) return response

        // Asegurar que tenemos la estructura correcta
        const data = apiResponse.data || {}

        return {
          error: false,
          msg: apiResponse.msg || '',
          data: {
            data: data.data || data || [], // Los reportes pueden estar en data.data o directamente en data
            totalItems: data.totalItems || data.total || (Array.isArray(data.data) ? data.data.length : Array.isArray(data) ? data.length : 0),
            page: data.page || page,
            itemsByPage: data.itemsByPage || limit,
          },
          status: 200,
        } as IWalletConfigList
      }),
      catchError(() => of(response)),
    )
  }

  // Generar corte manual
  generateCorte(data: { cutoffDate: string; clientIds: string[]; comments: string }): Observable<Blob> {
    return this.http
      .post(`${environment.apiUrl}${this.path}/cortes`, data, {
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          // El backend puede devolver un error como JSON dentro de un Blob
          if (error.error instanceof Blob && error.error.type === 'application/json') {
            return new Observable((observer) => {
              const reader = new FileReader()
              reader.onload = () => {
                const errorJson = JSON.parse(reader.result as string)
                observer.error(errorJson)
              }
              reader.onerror = () => {
                observer.error(error)
              }
              reader.readAsText(error.error)
            })
          }
          return of(error) // Propagar otros tipos de errores
        }),
      )
  }

  // Obtener detalles de un reporte específico
  getById(id: string): Observable<IWalletConfigResponse> {
    return this.http.get<IWalletConfigResponse>(`${environment.apiUrl}${this.path}/historial/${id}`).pipe(
      catchError(() =>
        of({
          error: true,
          msg: 'Reporte no encontrado',
          data: null,
        }),
      ),
    )
  }

  // Obtener reportes recientes
  getRecentReports(limit = 10): Observable<IWalletConfigResponse> {
    const params = new HttpParams().set('limit', limit.toString())

    return this.http.get<IWalletConfigResponse>(`${environment.apiUrl}${this.path}/historial-reciente`, { params }).pipe(
      catchError(() =>
        of({
          error: true,
          msg: 'Error al cargar reportes recientes',
          data: [],
        }),
      ),
    )
  }

  // Obtener estadísticas de reportes
  //* `reportType` acota las cifras a unos tipos. Esta pantalla manda siempre los cortes: sin ello
  //* encabezaba con el total de los SIETE tipos que comparten el historial desde F-098, encima de
  //* una tabla que solo enseña cortes.
  getStatistics(days = 30, reportType?: string): Observable<IWalletConfigResponse> {
    // Validar que days no exceda el máximo permitido
    const validDays = Math.min(Math.max(days, 1), 365)
    let params = new HttpParams().set('days', validDays.toString())
    if (reportType) params = params.set('reportType', reportType)

    return this.http.get<IWalletConfigResponse>(`${environment.apiUrl}${this.path}/estadisticas`, { params }).pipe(
      map((response) => {
        // Asegurar que la respuesta tenga la estructura correcta
        if (!response.error && response.data) {
          const data = response.data

          // Validar y normalizar campos obligatorios
          response.data = {
            totalReports: data.totalReports || 0,
            successfulReports: data.successfulReports || 0,
            failedReports: data.failedReports || 0,
            currentMonthReports: data.currentMonthReports || 0,
            avgProcessingTime: data.avgProcessingTime || '0.0',
            lastReportDate: data.lastReportDate || '',
            avgFileSize: data.avgFileSize || 'N/A',
            typeDistribution: data.typeDistribution || [],
          }
        }
        return response
      }),
      catchError(() =>
        of({
          error: true,
          msg: 'Error al cargar estadísticas',
          data: {
            totalReports: 0,
            successfulReports: 0,
            failedReports: 0,
            currentMonthReports: 0,
            avgProcessingTime: '0.0',
            lastReportDate: '',
            avgFileSize: 'N/A',
            typeDistribution: [],
          },
        }),
      ),
    )
  }

  // ========== NUEVOS MÉTODOS PARA CONFIGURACIONES DE CORTE ==========

  // Obtener todas las configuraciones de corte con filtros y paginación
  getAllConfigurations(options: ICutoffConfigFilterOptions): Observable<ICutoffConfigurationList> {
    const response: ICutoffConfigurationList = {
      error: true,
      msg: '',
      data: { data: [], totalItems: 0, totalPages: 0, currentPage: 1, itemsPerPage: 10 },
      status: 500,
    }

    return this.http.post<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config`, options).pipe(
      map((apiResponse) => {
        if (apiResponse.error) return response

        const data = apiResponse.data as any

        return {
          error: false,
          msg: apiResponse.msg || '',
          data: {
            data: data.data || [],
            totalItems: data.totalItems || 0,
            totalPages: data.totalPages || 0,
            currentPage: data.currentPage || options.page,
            itemsPerPage: data.itemsPerPage || options.limit,
          },
          status: 200,
        } as ICutoffConfigurationList
      }),
      catchError(() => of(response)),
    )
  }

  // Obtener configuraciones activas
  getActiveConfigurations(): Observable<ICutoffConfigurationResponse> {
    return this.http.get<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/active`).pipe(
      catchError(() =>
        of({
          error: true,
          msg: 'Error al cargar configuraciones activas',
          data: [],
        }),
      ),
    )
  }

  // Obtener configuración principal
  getPrimaryConfiguration(): Observable<ICutoffConfigurationResponse> {
    return this.http.get<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/primary`).pipe(
      catchError(() =>
        of({
          error: true,
          msg: 'Error al cargar configuración principal',
          data: null,
        }),
      ),
    )
  }

  // Obtener configuración por ID
  getConfigurationById(id: string): Observable<ICutoffConfigurationResponse> {
    return this.http.get<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/${id}`).pipe(
      catchError(() =>
        of({
          error: true,
          msg: 'Configuración no encontrada',
          data: null,
        }),
      ),
    )
  }

  // Crear nueva configuración de corte
  createConfiguration(data: ICreateCutoffConfigurationDto): Observable<ICutoffConfigurationResponse> {
    data.createdBy = this.userId
    return this.http.post<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/create`, data).pipe(
      catchError((error) =>
        of({
          error: true,
          msg: error.error?.message || 'Error al crear la configuración',
          data: null,
        }),
      ),
    )
  }

  // Actualizar configuración existente
  updateConfiguration(id: string, data: IUpdateCutoffConfigurationDto): Observable<ICutoffConfigurationResponse> {
    return this.http.put<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/${id}`, data).pipe(
      catchError((error) =>
        of({
          error: true,
          msg: error.error?.message || 'Error al actualizar la configuración',
          data: null,
        }),
      ),
    )
  }

  // Activar/Desactivar configuración
  toggleConfiguration(id: string): Observable<ICutoffConfigurationResponse> {
    return this.http.put<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/${id}/toggle`, {}).pipe(
      catchError((error) =>
        of({
          error: true,
          msg: error.error?.message || 'Error al cambiar estado de la configuración',
          data: null,
        }),
      ),
    )
  }

  // Eliminar configuración
  deleteConfiguration(id: string): Observable<ICutoffConfigurationResponse> {
    return this.http.delete<ICutoffConfigurationResponse>(`${environment.apiUrl}wallet/cutoff-config/${id}`).pipe(
      catchError((error) =>
        of({
          error: true,
          msg: error.error?.message || 'Error al eliminar la configuración',
          data: null,
        }),
      ),
    )
  }

  // Método para descargar archivo de reporte
  downloadReportFile(reportId: string): Observable<Blob> {
    return this.http
      .get(`${environment.apiUrl}wallet/config/historial/${reportId}/download`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${this.authService.getToken}`,
        },
      })
      .pipe(
        catchError((error) => {
          console.error('Error downloading report file:', error)
          throw error // Re-throw para que el componente maneje el error específico
        }),
      )
  }

  // Método auxiliar para extraer nombre de archivo del header Content-Disposition
  extractFilenameFromResponse(response: any, fallbackName: string = 'reporte.csv'): string {
    const contentDisposition = response.headers?.get('Content-Disposition')
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        return filenameMatch[1].replace(/['"]/g, '')
      }
    }
    return fallbackName
  }

  // Refrescar datos - usado para actualizar la tabla después de acciones
  refreshData(): void {
    this.refreshSubject.next()
  }
}
